import { isHostPage } from "@/features/standalone-versions";
import { useGetAccessibleLivePagesQuery } from "@/shared/services";
import { cn } from "@lib/cn";
import { IconChevronRight, IconFileAnalytics } from "@tabler/icons-react";
import { useCallback, useMemo, useState } from "react";
import { useAnalyticsStoreShallow } from "../store";

export const SlidebarStoriesList = () => {
  const [isOpen, setIsOpen] = useState(true);
  const { data: pages = [], isLoading: isLoadingLivePages } =
    useGetAccessibleLivePagesQuery();
  // A host page is not a story: it is the hidden page a user's standalone
  // versions hang off (see features/standalone-versions), and it would sit in
  // this list under its own title. The versions themselves are unaffected --
  // they are listed, filtered and counted by version, not by this page.
  const accessibleLivePages = useMemo(
    () => pages.filter((page) => !isHostPage(page)),
    [pages],
  );

  const { story, setStory } = useAnalyticsStoreShallow((s) => ({
    story: s.story,
    setStory: s.setStory,
  }));

  const handleStoryChange = useCallback(
    ({ id, title }: { id: string; title: string }) => {
      setStory({
        id,
        title,
      });
    },
    [setStory],
  );

  return (
    <div
      className={cn("tw-pb-1 tw-pt-4", {
        "tw-hidden": !accessibleLivePages?.length && !isLoadingLivePages,
      })}
    >
      <div className="tw-px-3">
        <button
          type="button"
          className="tw-flex tw-h-7 tw-w-full tw-cursor-pointer tw-items-center tw-justify-between tw-pl-2 tw-pr-1 tw-font-sans tw-text-xs tw-font-normal tw-text-blue tw-opacity-70 tw-outline-none tw-transition-opacity hover:tw-opacity-100"
          onClick={() => setIsOpen(!isOpen)}
        >
          <span>All stories</span>

          <IconChevronRight
            className={cn("tw-size-4 tw-text-blue tw-transition-transform", {
              "tw-rotate-90": isOpen,
            })}
          />
        </button>
      </div>
      <div
        className={cn("tw-grid tw-transition-[grid-template-rows]", {
          "[grid-template-rows:1fr]": isOpen,
          "[grid-template-rows:0fr]": !isOpen,
        })}
      >
        <div className="tw-overflow-hidden">
          <div className="tw-mb-1 tw-px-3 tw-pt-1.5 tw-space-y-1.5">
            {isLoadingLivePages ? (
              <SidebarSkeleton />
            ) : (
              accessibleLivePages.map((s) => (
                <button
                  type="button"
                  key={s._id}
                  onClick={() =>
                    handleStoryChange({ id: s._id, title: s.settings.title })
                  }
                  className={cn(
                    "tw-group/folder tw-relative tw-flex tw-h-7 tw-w-full tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded-lg tw-pl-2 tw-pr-1 tw-font-sans tw-text-sm tw-font-normal tw-text-blue tw-outline-none tw-transition-colors hover:tw-bg-blue-100",
                    story.id === s._id &&
                      "tw-bg-white tw-shadow-sidebar-menu hover:tw-bg-white",
                  )}
                >
                  <IconFileAnalytics className="tw-shrink-0" />
                  <span className="tw-text-sm tw-font-sans tw-text-blue tw-truncate tw-whitespace-nowrap">
                    {s.settings.title}
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const SKELETON_WIDTHS = [65, 80, 45, 90, 55];

const SidebarSkeleton = () => {
  return (
    <>
      {SKELETON_WIDTHS.map((width) => (
        <div
          key={width}
          className="tw-flex tw-items-center tw-gap-2 tw-h-7 tw-w-full tw-cursor-pointer tw-pl-2 tw-pr-1"
        >
          <span className="tw-size-4 tw-rounded tw-bg-blue-100 tw-animate-skeleton-loading"></span>
          <span
            className="tw-w-full tw-h-2 tw-rounded-xl tw-block tw-bg-blue-100 tw-animate-skeleton-loading"
            style={{ width: `${width}%` }}
          ></span>
        </div>
      ))}
    </>
  );
};
